import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import { cn } from '@/shared/lib/cn';
import { GlassEdge, glassSurface } from '@/shared/ui/glass';

import 'swiper/css';

interface WeekPickerProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  variant?: 'dark';
}

const DATE_PARAM = 'date';
const DATE_FORMAT = 'yyyy-MM-dd';
const DAYS_PER_PAGE = 7;
// Как на десктопе (бесконечная прокрутка календаря) — можно уйти и в прошлое,
// и далеко вперёд, а не только на 20 дней от сегодня.
const PAGES_BEFORE_TODAY = 4;
const PAGES_AFTER_TODAY = 12;
const PAGES_IN_RANGE = PAGES_BEFORE_TODAY + PAGES_AFTER_TODAY;
const DAYS_IN_RANGE = PAGES_IN_RANGE * DAYS_PER_PAGE;
const SLIDE_DURATION_MS = 250;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function clampDate(date: Date, min: Date, max: Date) {
  const day = startOfDay(date);

  if (day < min) {
    return min;
  }

  if (day > max) {
    return max;
  }

  return day;
}

function getDateKey(date: Date) {
  return format(date, DATE_FORMAT);
}

function getWeekdayLabel(date: Date) {
  return format(date, 'EEE', { locale: ru })
    .slice(0, 2)
    .replace(/^./, (char) => char.toUpperCase());
}

function getPageDays(days: Date[], pageIndex: number) {
  const start = pageIndex * DAYS_PER_PAGE;
  return days.slice(start, start + DAYS_PER_PAGE);
}

export function WeekPicker({
  selectedDate,
  onDateSelect,
  variant,
}: WeekPickerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const swiperRef = useRef<SwiperType | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);
  // Начало окна — на PAGES_BEFORE_TODAY недель раньше сегодня, а не с самого
  // сегодня: страница с сегодняшним днём не первая, а где-то в середине.
  const rangeStart = useMemo(
    () => subDays(today, PAGES_BEFORE_TODAY * DAYS_PER_PAGE),
    [today]
  );
  const minDate = rangeStart;
  const maxDate = useMemo(
    () => addDays(rangeStart, DAYS_IN_RANGE - 1),
    [rangeStart]
  );
  const days = useMemo(
    () =>
      Array.from({ length: DAYS_IN_RANGE }, (_, index) =>
        addDays(rangeStart, index)
      ),
    [rangeStart]
  );

  const getDateIndex = useCallback(
    (date: Date) =>
      clamp(
        differenceInCalendarDays(startOfDay(date), rangeStart),
        0,
        DAYS_IN_RANGE - 1
      ),
    [rangeStart]
  );

  const getPageIndex = useCallback(
    (date: Date) => Math.floor(getDateIndex(date) / DAYS_PER_PAGE),
    [getDateIndex]
  );

  const setDateParam = useCallback(
    (date: Date) => {
      const next = new URLSearchParams(searchParams);
      next.set(DATE_PARAM, getDateKey(date));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const dateParam = searchParams.get(DATE_PARAM);

    if (!dateParam) {
      setDateParam(selectedDate);
      return;
    }

    try {
      const dateFromParam = clampDate(parseISO(dateParam), minDate, maxDate);

      if (!isSameDay(dateFromParam, selectedDate)) {
        onDateSelect(dateFromParam);
      }

      if (dateParam !== getDateKey(dateFromParam)) {
        setDateParam(dateFromParam);
      }
    } catch {
      setDateParam(selectedDate);
    }
  }, [
    maxDate,
    minDate,
    onDateSelect,
    searchParams,
    selectedDate,
    setDateParam,
  ]);

  useEffect(() => {
    const swiper = swiperRef.current;
    const pageIndex = getPageIndex(selectedDate);

    if (swiper && swiper.activeIndex !== pageIndex) {
      swiper.slideTo(pageIndex, SLIDE_DURATION_MS);
    }
  }, [getPageIndex, selectedDate]);

  const handleDateSelect = (date: Date) => {
    onDateSelect(date);
    setDateParam(date);
    swiperRef.current?.slideTo(getPageIndex(date), SLIDE_DURATION_MS);
  };

  return (
    <div className="overflow-hidden rounded-full p-2">
      <Swiper
        className="w-full"
        slidesPerView={1}
        slidesPerGroup={1}
        spaceBetween={0}
        initialSlide={getPageIndex(selectedDate)}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {Array.from({ length: PAGES_IN_RANGE }, (_, pageIndex) => (
          <SwiperSlide key={pageIndex}>
            <div className="grid grid-cols-7 justify-items-center">
              {getPageDays(days, pageIndex).map((day) => {
                const isSelected = isSameDay(day, selectedDate);

                return (
                  <button
                    key={getDateKey(day)}
                    type="button"
                    className={cn(
                      variant === 'dark'
                        ? cn(
                            glassSurface,
                            'flex flex-col items-center justify-center rounded-full cursor-pointer text-white transition-[filter] hover:brightness-110'
                          )
                        : [
                            'flex size-14 flex-col items-center justify-center text-secondary-foreground rounded-full border text-sm cursor-pointer hover:bg-primary/10 hover:text-primary',
                            isSelected && 'bg-primary text-primary-foreground',
                          ]
                    )}
                    style={
                      variant === 'dark'
                        ? {
                            width: 46.68,
                            height: 46.68,
                            border: isSelected
                              ? '0.92px solid rgba(255, 255, 255, 0.5)'
                              : 'none',
                            // glassSurface даёт каждому кругу свою «парящую»
                            // тень (0 8px 22px) — семь кругов подряд сливают
                            // тени в сплошную тёмную полосу под рядом. Оставляем
                            // только лёгкое внутреннее свечение стекла.
                            boxShadow: isSelected
                              ? 'inset 0 -6px 10px rgba(255,255,255,0.04), 0 0 8px rgba(255, 255, 255, 0.2)'
                              : 'inset 0 -6px 10px rgba(255,255,255,0.04)',
                            fontSize: 12.82,
                            letterSpacing: '-0.46px',
                          }
                        : undefined
                    }
                    onClick={() => handleDateSelect(day)}
                    aria-label={`Select ${format(day, 'd MMMM', { locale: ru })}`}
                  >
                    {variant === 'dark' && <GlassEdge radius={23} />}
                    <span className="relative">{format(day, 'd')}</span>
                    <span className="relative">{getWeekdayLabel(day)}</span>
                  </button>
                );
              })}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
